import { useState, useEffect, useCallback } from "react";
import ReusableCard from "../../utils/reusable_card";

const API_URL = `${process.env.REACT_APP_API_URL}/api/tournaments/paginated`;

const PublicTournaments = () => {
  const [tournaments, setTournaments] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const [search, setSearch] = useState("");
  const [debounceSearch, setDebounceSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const hasMore = page < pages;

  const fetchTournaments = useCallback(
    async (targetPage = 1, replace = false) => {
      try {
        setLoading(true);
        setError(null);

        const resp = await fetch(
          `${API_URL}?page=${targetPage}&search=${debounceSearch}`,
        );

        if (!resp.ok) throw new Error("Error al cargar torneos");

        const json = await resp.json();

        const mapped = json.data.map((t) => ({
          id: t.id,
          name: t.tournament_name,
          city: t.city || "Sin ciudad",
          date: t.start_date,
          photo: t.picture_url,
        }));

        setTournaments((prev) => (replace ? mapped : [...prev, ...mapped]));

        setPages(json.pages);
        setPage(json.page);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [debounceSearch],
  );

  // First load + search changes
  useEffect(() => {
    fetchTournaments(1, true);
  }, [fetchTournaments]);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => setDebounceSearch(search), 300);
    return () => clearTimeout(handler);
  }, [search]);

  const handleLoadMore = () => hasMore && fetchTournaments(page + 1);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <header className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">
          Torneos disponibles
        </h1>

        <input
          type="text"
          className="border rounded-lg px-3 py-2 w-52 text-sm"
          placeholder="Buscar torneo..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </header>

      {error && (
        <div className="bg-red-100 text-red-700 px-3 py-2 mb-3 rounded-md border border-red-200">
          {error}
        </div>
      )}

      {!tournaments.length && !loading && (
        <p className="text-neutral-600 text-sm">No hay torneos disponibles.</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
        {tournaments.map((t) => (
          <a key={t.id} href={`/tournaments/${t.id}`}>
            <ReusableCard
              image={t.photo}
              name={t.name}
              city={t.city}
              date={t.date}
            />
          </a>
        ))}

        {loading && (
          <>
            <div className="animate-pulse rounded-2xl border border-neutral-200 p-3 h-52 bg-neutral-100" />
            <div className="animate-pulse rounded-2xl border border-neutral-200 p-3 h-52 bg-neutral-100" />
            <div className="animate-pulse rounded-2xl border border-neutral-200 p-3 h-52 bg-neutral-100" />
          </>
        )}
      </div>

      {hasMore && !loading && (
        <div className="flex justify-center mt-6">
          <button
            onClick={handleLoadMore}
            className="px-5 py-2.5 rounded-lg bg-black text-white hover:bg-neutral-800 transition text-sm"
          >
            Ver más
          </button>
        </div>
      )}
    </div>
  );
};

export default PublicTournaments;
